import React, { useState, useEffect, useImperativeHandle } from 'react'
import {
  DESIGN_TYPE,
  initCanvaAPI,
  loadCanvaSdk,
  TCanvaButtonApi,
  TCanvaDesignType,
  TCustomCanvaDesignType
} from '../../../services/canva'

import './CanvaButton.scss'

interface CanvaButtonProps {
  updateImageData: (payload: TCanvaPayload) => void
  designId?: string
}

export type TCanvaPayload = {
  designId: string
  exportUrl: string
}

export type CanvaButtonActions = {
  createDesign: (canvaSettings: TCustomCanvaDesignType | TCanvaDesignType) => void
}

const CanvaButton = React.forwardRef<CanvaButtonActions, CanvaButtonProps>(
  ({ updateImageData, designId }, ref) => {
    const [canvaApi, setCanvaApi] = useState<TCanvaButtonApi | null>(null)

    useEffect(() => {
      const init = async () => {
        await loadCanvaSdk()
        setTimeout(async () => {
          const api = await initCanvaAPI()
          setCanvaApi(api)
        }, 1000)
      }
      init()
    }, [])

    useImperativeHandle(ref, () => ({
      createDesign: (canvaSettings: TCustomCanvaDesignType | TCanvaDesignType) => {
        const { type } = canvaSettings
        if (type === DESIGN_TYPE.CUSTOM_TYPE) {
          const { measure, width, height } = canvaSettings
          canvaApi!.createDesign({
            design: {
              type: 'SocialMedia',
              dimensions: {
                width,
                height,
                units: measure
              }
            },
            onDesignOpen: ({ designId }) => {
              // Triggered when editor finishes loading and opens a new design.
              // You can save designId for future use.
            },
            onDesignPublish: ({ exportUrl, designId }) => {
              // Triggered when design is published to an image.
              const payload: TCanvaPayload = {
                exportUrl,
                designId
              }
              updateImageData(payload)
              // Save the image to your server as the exportUrl will expire shortly.
            },
            onDesignClose: () => {
              // Triggered when editor is closed.
            }
          })
        } else if (type === DESIGN_TYPE.CANVA_TYPE) {
          const { measure, width, height, name } = canvaSettings as TCanvaDesignType
          canvaApi!.createDesign({
            design: {
              type: name,
              dimensions: {
                width,
                height,
                units: measure
              }
            },
            onDesignOpen: ({ designId }) => {
              // Triggered when editor finishes loading and opens a new design.
              // You can save designId for future use.
            },
            onDesignPublish: ({ exportUrl, designId }) => {
              // Triggered when design is published to an image.
              const payload: TCanvaPayload = {
                exportUrl,
                designId
              }
              updateImageData(payload)
              // Save the image to your server as the exportUrl will expire shortly.
            },
            onDesignClose: () => {
              // Triggered when editor is closed.
            }
          })
        }
      }
    }))

    const createDesign = () => {
      canvaApi!.createDesign({
        design: {
          type: 'SocialMedia'
        },
        onDesignOpen: ({ designId }) => {
          // Triggered when editor finishes loading and opens a new design.
          // You can save designId for future use.
        },
        onDesignPublish: ({ exportUrl, designId }) => {
          // Triggered when design is published to an image.
          const payload: TCanvaPayload = {
            exportUrl,
            designId
          }
          updateImageData(payload)
          // Save the image to your server as the exportUrl will expire shortly.
        },
        onDesignClose: () => {
          // Triggered when editor is closed.
        }
      })
    }

    const editDesign = (designId: string) => {
      canvaApi!.editDesign({
        design: {
          id: designId
        },
        onDesignOpen: ({ designId }) => {
          // Triggered when editor finishes loading and opens a new design.
          // You can save designId for future use.
        },
        onDesignPublish: ({ exportUrl, designId }) => {
          // Triggered when design is published to an image.
          const payload: TCanvaPayload = {
            exportUrl,
            designId
          }
          updateImageData(payload)
          // Save the image to your server as the exportUrl will expire shortly.
        },
        onDesignClose: () => {
          // Triggered when editor is closed.
        }
      })
    }

    const onClick = () => {
      designId ? editDesign(designId) : createDesign()
    }

    return (
      <span
        className={`canva-btn canva-btn-theme-default canva-btn-size-s ${
          !canvaApi && 'canva-btn-disabled'
        }`}
        onClick={onClick}
      >
        <span className='canva-btn-i'></span>
      </span>
    )
  }
)

export default CanvaButton
